import {
  IMAP_DEFAULT_PORT,
  type IntegrationItemDto,
  type IntegrationMode,
  imapConnectionChanged,
  integrationSecretSchema,
  type MailboxScope,
  mailboxPasswordSchema,
  SMTP_DEFAULT_PORT,
} from '@fos/shared/contracts/integrations';
import {
  IMAP_FORM_HINTS,
  INTEGRATION_SIMULATED_HINT,
  integrationProviderLabel,
  MAILBOX_SCOPE_DESCRIPTIONS,
  MAILBOX_SCOPE_LABELS,
} from '@fos/shared/labels';
import { type ChangeEvent, type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, PasswordInput } from '../../../ui/Input';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { isMailboxKind, kindLabel, testResultToast } from './integrations-ui';

/**
 * Okna Ustawienia → Integracje: połączenie (symulacja ↔ realny dostawca; skrzynka IMAP/SMTP — rodzaj firmowa /
 * osobista, adres, serwery i porty z panelu hostingu, login, hasło), klucz API, ustawienia połączenia, potwierdzenie
 * (odłączenie, przejście na symulację). Klucz API i hasło skrzynki: pole typu password, nigdy wypełnione zapisaną
 * wartością (serwer jej nie zwraca), czyszczone przy zamknięciu; po zapisie od razu test połączenia. Zmiany wymagają
 * potwierdzenia hasłem (modal step-up z linku tRPC).
 */

const FIELD_KEYS = [
  'provider',
  'secret',
  'domain',
  'intervalSec',
  'calendarId',
  'password',
  'mailboxScope',
  'account',
  'imapHost',
  'imapPort',
  'smtpHost',
  'smtpPort',
  'login',
] as const;
type FieldErrors = Partial<Record<(typeof FIELD_KEYS)[number], string>>;

const SECRET_HINT = 'Zapisujemy go zaszyfrowany. Po zapisie widać tylko 4 ostatnie znaki.';

function fieldErrors(err: unknown): FieldErrors {
  const out: FieldErrors = {};
  for (const issue of validationIssues(err)) {
    const key = issue.path.replace(/^config\./, '') as keyof FieldErrors;
    if ((FIELD_KEYS as readonly string[]).includes(key) && !out[key]) {
      out[key] = issue.message;
    }
  }
  return out;
}

function useAfterSave() {
  const utils = trpc.useUtils();
  const test = trpc.integrations.test.useMutation();
  return async (id: string | null, opts: { runTest: boolean }) => {
    await utils.integrations.list.invalidate();
    if (!opts.runTest || !id) return;
    try {
      testResultToast(await test.mutateAsync({ id }));
    } catch (err) {
      if (appErrorCode(err) !== 'STEP_UP_REQUIRED') toast.error(errorMessage(err));
    } finally {
      await utils.integrations.list.invalidate();
    }
  };
}

function FormError({ children }: { children: ReactNode }) {
  return children ? <FormMessage>{children}</FormMessage> : null;
}

// ── Połączenie: symulacja albo realny dostawca ─────────────────────────────

interface MailboxForm {
  scope: MailboxScope;
  account: string;
  imapHost: string;
  imapPort: string;
  smtpHost: string;
  smtpPort: string;
  login: string;
}

function mailboxFormOf(item: IntegrationItemDto): MailboxForm {
  const c = item.config;
  const text = (v: unknown) => (typeof v === 'string' ? v : '');
  const port = (v: unknown, fallback: number) => String(typeof v === 'number' ? v : fallback);
  return {
    // nowa skrzynka domyślnie firmowa (osobista tylko z jawnym wyborem)
    scope: item.id !== null && item.ownerUserId ? 'personal' : 'company',
    account: text(c.account),
    imapHost: text(c.imapHost),
    imapPort: port(c.imapPort, IMAP_DEFAULT_PORT),
    smtpHost: text(c.smtpHost),
    smtpPort: port(c.smtpPort, SMTP_DEFAULT_PORT),
    login: text(c.login),
  };
}

function portValue(value: string): number | string {
  const trimmed = value.trim();
  return /^\d{1,5}$/.test(trimmed) ? Number(trimmed) : trimmed;
}

export function ConnectDialog({
  open,
  onOpenChange,
  item,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: IntegrationItemDto;
}) {
  const switchMode = trpc.integrations.switchMode.useMutation();
  const afterSave = useAfterSave();
  const providers = item.providers.filter((p) => p.auth === 'api_key' || p.auth === 'none' || p.auth === 'password');
  // kalendarz łączy się wyłącznie kontem Google (bez wyboru trybu w tym oknie)
  const googleOnly = item.kind === 'google_calendar';
  const mailbox = isMailboxKind(item.kind);
  const [mode, setMode] = useState<IntegrationMode>('simulated');
  const [provider, setProvider] = useState<string>('');
  const [secret, setSecret] = useState('');
  const [domain, setDomain] = useState('');
  const [box, setBox] = useState<MailboxForm>(() => mailboxFormOf(item));
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu (i czyszczenie klucza / hasła przy zamknięciu)
  useEffect(() => {
    setSecret('');
    setPassword('');
    if (!open) return;
    // nowa integracja: najpierw symulacja; istniejąca i każda skrzynka: realne połączenie
    setMode(googleOnly || (item.id === null && !mailbox) ? 'simulated' : 'real');
    const current = providers.find((p) => p.provider === item.provider)?.provider;
    setProvider(current ?? providers[0]?.provider ?? '');
    setDomain(typeof item.config.domain === 'string' ? item.config.domain : '');
    setBox(mailboxFormOf(item));
    setErrors({});
    setFormError(null);
  }, [open]);

  const auth = providers.find((p) => p.provider === provider)?.auth ?? null;
  const fakturownia = mode === 'real' && provider === 'fakturownia';
  const imap = mode === 'real' && auth === 'password';
  const boxConfig = {
    account: box.account.trim(),
    imapHost: box.imapHost.trim(),
    imapPort: portValue(box.imapPort),
    smtpHost: box.smtpHost.trim(),
    smtpPort: portValue(box.smtpPort),
    login: box.login.trim() || null,
  };
  // nowy serwer, port albo login skrzynki = hasło od nowa (zapisane nie idzie na nowy serwer; serwer też to sprawdza)
  const serverChanged = imap && item.provider === provider && imapConnectionChanged(item.config, boxConfig);
  const keepsSecret = item.provider === provider && item.hasSecret && !serverChanged;
  const setField = (key: keyof MailboxForm) => (e: ChangeEvent<HTMLInputElement>) =>
    setBox((b) => ({ ...b, [key]: e.target.value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    const input: Record<string, unknown> = item.id
      ? { id: item.id, version: item.version ?? undefined, mode }
      : { kind: item.kind, mode };
    if (mode === 'real') {
      input.provider = provider;
      if (fakturownia) input.config = { domain: domain.trim() };
      if (auth === 'api_key' && (secret || !keepsSecret)) {
        const parsed = integrationSecretSchema.safeParse(secret);
        if (!parsed.success) return setErrors({ secret: parsed.error.issues[0]?.message ?? 'Wpisz klucz API' });
        input.secret = parsed.data;
      }
      if (imap) {
        input.mailboxScope = box.scope;
        input.config = boxConfig;
        if (password || !keepsSecret) {
          const parsed = mailboxPasswordSchema.safeParse(password);
          if (!parsed.success) return setErrors({ password: parsed.error.issues[0]?.message ?? 'Wpisz hasło' });
          input.password = parsed.data;
        }
      }
    }
    try {
      const saved = await switchMode.mutateAsync(input as never);
      setSecret('');
      setPassword('');
      onOpenChange(false);
      if (mode === 'simulated') {
        toast.success('Włączono symulację', { description: INTEGRATION_SIMULATED_HINT });
        await afterSave(saved.id, { runTest: false });
      } else {
        toast.success(`Zapisano: ${integrationProviderLabel(provider)}`);
        await afterSave(saved.id, { runTest: true });
      }
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'STEP_UP_REQUIRED') return setFormError('Zmiana wymaga potwierdzenia hasłem. Spróbuj ponownie.');
      const fe = fieldErrors(err);
      if (code === 'VALIDATION_ERROR' && Object.keys(fe).length > 0) return setErrors(fe);
      setFormError(
        code === 'CONFLICT' && !imap
          ? 'Ktoś zmienił tę integrację. Zamknij okno i spróbuj ponownie.'
          : errorMessage(err),
      );
    }
  };

  const fromReal = item.id !== null && !item.simulated && item.status !== 'disconnected';
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="m"
      title={
        item.id === null && mailbox && item.key.startsWith('new:') ? 'Nowa skrzynka pocztowa' : kindLabel(item.kind)
      }
      description={
        imap
          ? IMAP_FORM_HINTS.intro
          : 'Symulacja działa na danych przykładowych. Realne połączenie wymaga konta u dostawcy.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={switchMode.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="integration-connect-form" loading={switchMode.isPending}>
            {mode === 'simulated' ? 'Włącz symulację' : 'Zapisz i sprawdź'}
          </Button>
        </>
      }
    >
      <form
        id="integration-connect-form"
        onSubmit={submit}
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4"
      >
        {!googleOnly && (
          <Segmented
            aria-label="Tryb integracji"
            fullWidth
            value={mode}
            onValueChange={(v) => {
              setMode(v);
              setErrors({});
            }}
            options={[
              { value: 'simulated', label: 'Symulacja' },
              { value: 'real', label: 'Realne połączenie' },
            ]}
          />
        )}
        {mode === 'simulated' ? (
          <p className="text-body-s text-fg-secondary">
            {INTEGRATION_SIMULATED_HINT}.{fromReal ? ' Zapisany klucz albo hasło zostanie usunięte.' : ''}
            {googleOnly ? ' Konto Google połączysz przyciskiem „Połącz z Google”.' : ''}
          </p>
        ) : (
          <>
            {providers.length > 0 && (
              <Field label="Dostawca" error={errors.provider}>
                <Select
                  value={provider || undefined}
                  onValueChange={(v) => {
                    setProvider(v);
                    setSecret('');
                    setPassword('');
                  }}
                  options={providers.map((p) => ({ value: p.provider, label: integrationProviderLabel(p.provider) }))}
                />
              </Field>
            )}
            {fakturownia && (
              <Field
                label="Subdomena konta"
                hint="Z adresu konta: fravity.fakturownia.pl → fravity"
                error={errors.domain}
              >
                <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="fravity" />
              </Field>
            )}
            {auth === 'api_key' && (
              <Field
                label={provider === 'fakturownia' ? 'Token API' : 'Klucz API'}
                hint={keepsSecret ? `Zostaw puste, aby zachować zapisany klucz. ${SECRET_HINT}` : SECRET_HINT}
                error={errors.secret}
              >
                <PasswordInput
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  autoComplete="new-password"
                  placeholder={keepsSecret && item.secretLast4 ? `…${item.secretLast4}` : undefined}
                />
              </Field>
            )}
            {imap && (
              <>
                <Field label="Rodzaj skrzynki" hint={MAILBOX_SCOPE_DESCRIPTIONS[box.scope]} error={errors.mailboxScope}>
                  <Segmented
                    aria-label="Rodzaj skrzynki"
                    fullWidth
                    value={box.scope}
                    onValueChange={(v) => setBox((b) => ({ ...b, scope: v }))}
                    options={[
                      { value: 'company', label: MAILBOX_SCOPE_LABELS.company },
                      { value: 'personal', label: MAILBOX_SCOPE_LABELS.personal },
                    ]}
                  />
                </Field>
                <Field label="Adres skrzynki" error={errors.account}>
                  <Input
                    type="email"
                    value={box.account}
                    onChange={setField('account')}
                    placeholder="kontakt@twojadomena.pl"
                    autoComplete="off"
                  />
                </Field>
                <div className="grid grid-cols-[1fr_6.5rem] gap-3">
                  <Field label="Serwer IMAP" hint={IMAP_FORM_HINTS.imapHost} error={errors.imapHost}>
                    <Input value={box.imapHost} onChange={setField('imapHost')} placeholder="z panelu hostingu" />
                  </Field>
                  <Field label="Port IMAP" hint={IMAP_FORM_HINTS.imapPort} error={errors.imapPort}>
                    <Input value={box.imapPort} onChange={setField('imapPort')} inputMode="numeric" mono />
                  </Field>
                </div>
                <div className="grid grid-cols-[1fr_6.5rem] gap-3">
                  <Field label="Serwer SMTP" hint={IMAP_FORM_HINTS.smtpHost} error={errors.smtpHost}>
                    <Input value={box.smtpHost} onChange={setField('smtpHost')} placeholder="z panelu hostingu" />
                  </Field>
                  <Field label="Port SMTP" hint={IMAP_FORM_HINTS.smtpPort} error={errors.smtpPort}>
                    <Input value={box.smtpPort} onChange={setField('smtpPort')} inputMode="numeric" mono />
                  </Field>
                </div>
                <Field label="Login" hint={IMAP_FORM_HINTS.login} error={errors.login}>
                  <Input
                    value={box.login}
                    onChange={setField('login')}
                    placeholder={box.account.trim() || 'adres skrzynki'}
                    autoComplete="off"
                  />
                </Field>
                <Field
                  label="Hasło skrzynki"
                  hint={
                    keepsSecret
                      ? IMAP_FORM_HINTS.passwordKeep
                      : serverChanged && item.hasSecret
                        ? IMAP_FORM_HINTS.passwordServerChanged
                        : IMAP_FORM_HINTS.password
                  }
                  error={errors.password}
                >
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder={keepsSecret ? 'Zapisane' : undefined}
                  />
                </Field>
              </>
            )}
            {auth === 'none' && <p className="text-body-s text-fg-secondary">Ten dostawca nie wymaga klucza.</p>}
          </>
        )}
        <FormError>{formError}</FormError>
      </form>
    </Dialog>
  );
}

// ── Klucz API ──────────────────────────────────────────────────────────────

export function SecretDialog({
  open,
  onOpenChange,
  item,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: IntegrationItemDto;
}) {
  const setSecretMutation = trpc.integrations.setSecret.useMutation();
  const afterSave = useAfterSave();
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: czyszczenie klucza i błędów przy każdym otwarciu i zamknięciu
  useEffect(() => {
    setSecret('');
    setError(null);
    setFormError(null);
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFormError(null);
    const parsed = integrationSecretSchema.safeParse(secret);
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Wpisz klucz API');
    if (!item.id) return;
    try {
      await setSecretMutation.mutateAsync({ id: item.id, secret: parsed.data });
      setSecret('');
      onOpenChange(false);
      toast.success('Zapisano klucz');
      await afterSave(item.id, { runTest: true });
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'STEP_UP_REQUIRED')
        return setFormError('Zmiana klucza wymaga potwierdzenia hasłem. Spróbuj ponownie.');
      const fe = fieldErrors(err);
      if (fe.secret) return setError(fe.secret);
      setFormError(errorMessage(err));
    }
  };

  const label = item.provider === 'fakturownia' ? 'Token API' : 'Klucz API';
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="s"
      title={`${item.hasSecret ? 'Zmień' : 'Ustaw'} ${item.provider === 'fakturownia' ? 'token API' : 'klucz API'}`}
      description={`${kindLabel(item.kind)} · ${item.provider ? integrationProviderLabel(item.provider) : ''}. Zapisanego klucza nie pokazujemy.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={setSecretMutation.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="integration-secret-form" loading={setSecretMutation.isPending}>
            Zapisz i sprawdź
          </Button>
        </>
      }
    >
      <form
        id="integration-secret-form"
        onSubmit={submit}
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4"
      >
        <Field label={label} hint={SECRET_HINT} error={error}>
          <PasswordInput
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="new-password"
            placeholder={item.secretLast4 ? `…${item.secretLast4}` : undefined}
          />
        </Field>
        <FormError>{formError}</FormError>
      </form>
    </Dialog>
  );
}

// ── Ustawienia połączenia (konfiguracja bez sekretów) ─────────────────────

const INTERVAL_OPTIONS = [60, 120, 300, 600, 900, 1800, 3600].map((sec) => ({
  value: String(sec),
  label: sec < 3600 ? `co ${sec / 60} min` : 'co godzinę',
}));

/** Rodzaje z polami konfiguracji w aplikacji. */
export function hasEditableConfig(item: IntegrationItemDto): boolean {
  return (
    item.kind === 'monitoring' ||
    item.kind === 'google_calendar' ||
    (item.kind === 'invoicing' && item.provider === 'fakturownia')
  );
}

export function ConfigDialog({
  open,
  onOpenChange,
  item,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: IntegrationItemDto;
}) {
  const setConfig = trpc.integrations.setConfig.useMutation();
  const utils = trpc.useUtils();
  const [domain, setDomain] = useState('');
  const [interval, setIntervalSec] = useState('120');
  const [calendarId, setCalendarId] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset przy otwarciu
  useEffect(() => {
    if (!open) return;
    setDomain(typeof item.config.domain === 'string' ? item.config.domain : '');
    setIntervalSec(typeof item.config.intervalSec === 'number' ? String(item.config.intervalSec) : '120');
    setCalendarId(typeof item.config.calendarId === 'string' ? item.config.calendarId : '');
    setErrors({});
    setFormError(null);
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!item.id || item.version === null) return;
    setErrors({});
    setFormError(null);
    const config: Record<string, unknown> = {};
    if (item.kind === 'invoicing') config.domain = domain.trim();
    if (item.kind === 'monitoring') config.intervalSec = Number(interval);
    if (item.kind === 'google_calendar') config.calendarId = calendarId.trim() || null;
    try {
      await setConfig.mutateAsync({ id: item.id, version: item.version, config });
      toast.success('Zapisano ustawienia');
      await utils.integrations.list.invalidate();
      onOpenChange(false);
    } catch (err) {
      const code = appErrorCode(err);
      if (code === 'STEP_UP_REQUIRED') return setFormError('Zmiana wymaga potwierdzenia hasłem. Spróbuj ponownie.');
      const fe = fieldErrors(err);
      if (code === 'VALIDATION_ERROR' && Object.keys(fe).length > 0) return setErrors(fe);
      setFormError(
        code === 'CONFLICT' ? 'Ktoś zmienił tę integrację. Zamknij okno i spróbuj ponownie.' : errorMessage(err),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="s"
      title="Ustawienia połączenia"
      description={kindLabel(item.kind)}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={setConfig.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="integration-config-form" loading={setConfig.isPending}>
            Zapisz
          </Button>
        </>
      }
    >
      <form id="integration-config-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        {item.kind === 'invoicing' && (
          <Field label="Subdomena konta" hint="Z adresu konta: fravity.fakturownia.pl → fravity" error={errors.domain}>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="fravity" />
          </Field>
        )}
        {item.kind === 'monitoring' && (
          <Field label="Domyślny odstęp sprawdzeń" hint="Dla nowych monitorów stron" error={errors.intervalSec}>
            <Select value={interval} onValueChange={setIntervalSec} options={INTERVAL_OPTIONS} />
          </Field>
        )}
        {item.kind === 'google_calendar' && (
          <Field label="Kalendarz" hint="Puste = kalendarz główny konta" error={errors.calendarId}>
            <Input value={calendarId} onChange={(e) => setCalendarId(e.target.value)} placeholder="primary" />
          </Field>
        )}
        <FormError>{formError}</FormError>
      </form>
    </Dialog>
  );
}

// ── Potwierdzenie ──────────────────────────────────────────────────────────

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  pending: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="s"
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Anuluj
          </Button>
          <Button variant="primary" loading={pending} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
